import {
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { api } from "./api";

/**
 * Frontend view of the active learner. Mirrors the backend
 * `MeResponse` DTO (snake_case `picture_url` is camel-cased to
 * `pictureUrl` by Litestar's DTO rename strategy).
 */
export type CurrentUser = {
  id: number;
  email: string;
  name: string;
  role: "user" | "admin" | (string & {});
  pictureUrl: string | null;
};

/**
 * Single query-key shared between the React Query hook and the
 * TanStack Router `beforeLoad` auth guard. Keeping this in one place
 * means the route-level fetch primes the same cache the components read.
 */
export const currentUserQueryKey = ["current-user"] as const;

async function fetchCurrentUser(): Promise<CurrentUser | null> {
  const { data, error, response } = await api.GET("/auth/me");
  if (error) {
    // Litestar's "never" typing for the error object hides the HTTP
    // status the openapi-fetch client surfaces on the response — read
    // it off the Fetch response instead.
    if ((response as Response | undefined)?.status === 401) return null;
    throw error;
  }
  return data as CurrentUser;
}

/**
 * Resolve the current user, hitting the network at most once per
 * staleTime window. Used by router `beforeLoad` hooks (which run
 * outside React) so a single source of truth feeds both the route
 * guard and `useCurrentUser` in components.
 */
export async function ensureCurrentUser(
  queryClient: QueryClient,
): Promise<CurrentUser | null> {
  return queryClient.ensureQueryData({
    queryKey: currentUserQueryKey,
    queryFn: fetchCurrentUser,
    staleTime: 5 * 60_000,
  });
}

/**
 * Read the active learner from `/auth/me`. Returns the resolved user
 * once the query settles; until then, `undefined`. Treats network +
 * 401 errors the same — null means "not signed in". When the backend
 * fallback in auth.py:131-139 is removed (after OAuth ships), 401
 * becomes the load-bearing signal that the router redirects on.
 */
export function useCurrentUser() {
  return useQuery({
    queryKey: currentUserQueryKey,
    queryFn: fetchCurrentUser,
    // Sessions are JWT-cookie-backed and 30-day TTL'd; once we have
    // the user, refetching aggressively just churns the network.
    staleTime: 5 * 60_000,
  });
}

/**
 * Dev-stub login. Posts `{email, name}` to `/auth/dev-login`, which
 * sets the session cookie and returns the user payload. On success
 * we invalidate the `current-user` query so the UI re-renders with
 * the freshly logged-in user.
 *
 * Real Google OAuth replaces this with a redirect to
 * `/auth/google/login` — until then, this is the only way to log
 * in as a non-default user.
 */
export function useDevLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { email: string; name: string }) => {
      const { data, error } = await api.POST("/auth/dev-login", {
        body: vars,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      // Wipe React Query state: not only does `current-user` need to
      // refetch, but any cached per-user data (skills, stats, etc.)
      // from the previous identity must NOT bleed into the new
      // session. Same `queryClient.clear()` pattern the language
      // switcher uses.
      queryClient.clear();
    },
  });
}

/**
 * Sign out. Clears the cookie server-side; we also clear React
 * Query so the UI re-fetches under whatever the new identity is
 * (today, the Jorge-fallback).
 */
export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { error } = await api.POST("/auth/logout", {});
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.clear();
    },
  });
}

/** True when the current user has `role === 'admin'`. Used by the
 *  `<AdminOnly>` wrapper + the header's admin-link logic. */
export function useIsAdmin(): boolean {
  const { data } = useCurrentUser();
  return data?.role === "admin";
}
